import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Stack, Typography } from '@mui/material';

import Header from 'components/Common/Header';
import CardWrapper from 'components/Common/CardWrapper';
import Table from 'components/Common/StyledTable';
import Button from 'components/Common/Button';
import MuiLoader from 'components/Common/Loader';
import { selectCanAccessPortal, selectSelectedParishId } from 'redux/selectors/parishContext';
import { AppDispatch, RootState } from 'redux/store';
import { updateTableState } from 'redux/slices/common/appSlice';
import { clearSentInvoices, fetchSentInvoices } from 'redux/slices/wallet/walletInvoicesSlice';
import type { PaymentRequest } from 'types/paymentRequests';
import routes from 'constants/routes';
import { dateFormat, formatAmount, timeFormat } from 'utils/helpers';
import WalletSectionTabs from 'components/Wallet/WalletSectionTabs';

const columns = [
    {
        id: 'createdAt',
        label: 'Date',
        render: ({ data }: { data: PaymentRequest }) => `${dateFormat(data.createdAt)} ${timeFormat(data.createdAt, 'hh:mm A')}`,
    },
    {
        id: 'subject',
        label: 'Subject',
        render: ({ data }: { data: PaymentRequest }) => data.subject || '—',
    },
    {
        id: 'sender',
        label: 'Sent by',
        render: ({ data }: { data: PaymentRequest }) =>
            (data.sender && [data.sender.firstName, data.sender.lastName].filter(Boolean).join(' ')) || '—',
    },
    {
        id: 'amountCents',
        label: 'Amount',
        align: 'right' as const,
        render: ({ data }: { data: PaymentRequest }) => formatAmount(data.amountCents, 'cents'),
    },
    {
        id: 'currency',
        label: 'Currency',
        render: ({ data }: { data: PaymentRequest }) => (data.currency || '').toUpperCase() || '—',
    },
    {
        id: 'status',
        label: 'Status',
        render: ({ data }: { data: PaymentRequest }) => data.status || '—',
    },
    {
        id: 'id',
        label: 'Invoice ID',
        render: ({ data }: { data: PaymentRequest }) => {
            const id = data.id || '';
            const short = id.length > 8 ? `${id.slice(0, 8)}…` : id;
            return (
                <Typography component="span" title={id} sx={{ fontSize: 14 }}>
                    {short || '—'}
                </Typography>
            );
        },
    },
];

/**
 * Parish-wide invoice history: every payment request sent by any staff member of the
 * current parish (GET /v1/parishes/:parishId/payment-requests), not just the caller's own.
 * Row click → existing PaymentRequestDetail at /payment-requests/:id.
 */
export default function InvoiceHistory() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const canAccessPortal = useSelector(selectCanAccessPortal);
    const parishId = useSelector(selectSelectedParishId);
    const { pageNumber, pageSize } = useSelector((state: RootState) => state.app.table);
    const { items, total, loading, error } = useSelector((state: RootState) => state.wallet.walletInvoices);

    // Shared StyledTable pagination can be leftover from another screen — start this list on page 1.
    useEffect(() => {
        dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
    }, [dispatch, pageSize]);

    // Parish switch: reset back to page 1 (mirrors Reports.tsx) — a stale page N from a
    // previous, larger parish must never carry over onto a smaller one.
    useEffect(() => {
        if (!parishId) return;
        if (pageNumber !== 0) {
            dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to parish switches
    }, [parishId, dispatch]);

    useEffect(() => {
        if (!canAccessPortal || !parishId) return;
        dispatch(
            fetchSentInvoices({
                parishId,
                page: pageNumber + 1,
                pageSize,
            })
        );
    }, [dispatch, canAccessPortal, parishId, pageNumber, pageSize]);

    useEffect(() => {
        return () => {
            dispatch(clearSentInvoices());
        };
    }, [dispatch]);

    const goToDetail = (row: PaymentRequest) => {
        if (!row?.id) return;
        navigate(routes.paymentRequests.detail.replace(':id', row.id));
    };

    const goToSend = () => navigate(routes.sendinvoice.main);

    if (!canAccessPortal) {
        return (
            <Box sx={{ backgroundColor: '#F2F2F6', flex: 1, minHeight: '100%' }}>
                <Box sx={{ p: 2.75 }}>
                    <Header />
                </Box>
                <CardWrapper>
                    <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400 }} variant="h1">
                        Invoices
                    </Typography>
                    <Typography variant="body1">
                        You need a parish staff role (Clerk or higher) to view this parish&apos;s invoices.
                    </Typography>
                </CardWrapper>
            </Box>
        );
    }

    if (!parishId) {
        return (
            <Box sx={{ backgroundColor: '#F2F2F6', flex: 1, minHeight: '100%' }}>
                <Box sx={{ p: 2.75 }}>
                    <Header />
                </Box>
                <Box sx={{ px: 2.75, pb: 3 }}>
                    <Alert severity="warning">Select a parish to view invoices.</Alert>
                </Box>
            </Box>
        );
    }

    const showEmpty = !loading && !error && (items?.length || 0) === 0;
    const showTable = !showEmpty && !error;

    return (
        <Box sx={{ backgroundColor: '#F2F2F6', flex: 1, minHeight: '100%' }}>
            <Box sx={{ p: 2.75 }}>
                <Header />
            </Box>
            <Box sx={{ px: 2.75, pb: 3 }}>
                <CardWrapper maxWidth={false} disableGutters>
                    <WalletSectionTabs current="invoices" />
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        spacing={2}
                        sx={{ mb: 2, mt: 2 }}
                    >
                        <Typography sx={{ fontSize: { xs: 18, sm: 24 }, color: '#220E40', fontWeight: 500 }} variant="h1">
                            Invoices
                        </Typography>
                        <Button title="Send Invoice" onClick={goToSend} sx={{ width: { xs: '100%', sm: 180 } }} />
                    </Stack>
                    <Typography sx={{ mb: 2 }} color="text.secondary" variant="body2">
                        Payment requests sent by this parish&apos;s staff. Open a row to view, cancel, or resend.
                    </Typography>

                    {error && !loading && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading && (items?.length || 0) === 0 && (
                        <Box sx={{ position: 'relative', minHeight: 200 }}>
                            <MuiLoader absolute loading />
                        </Box>
                    )}

                    {showEmpty && (
                        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }} spacing={1}>
                            <Typography sx={{ fontSize: { xs: 20, sm: 28 }, color: 'gray' }}>No invoices yet</Typography>
                            <Typography color="text.secondary">Invoices your parish&apos;s staff send will appear here.</Typography>
                            <Button title="Send Invoice" onClick={goToSend} sx={{ mt: 2, width: { xs: '80%', sm: 220 } }} />
                        </Stack>
                    )}

                    {showTable && (items?.length || 0) > 0 && (
                        <Table
                            unStyled
                            navigationHandler={goToDetail}
                            columns={columns}
                            data={items}
                            totalCount={total}
                            loading={loading}
                            serverSidePagination
                        />
                    )}
                </CardWrapper>
            </Box>
        </Box>
    );
}
